import React, { useState, useEffect, useMemo } from 'react'
import { GraniteDataGrid } from '@tecuity/granite-ui/GraniteDataGrid'
import * as api from '../../requests'
import { useDispatch } from 'react-redux'
import { setErrorToast, setSuccessToast } from '../../actions'
import { SelectBox, ContextButton, ButtBox } from '@tecuity/granite-ui'
import './ManageRecordUserAccess.scss'
import { Spinner } from '../Loaders/Loaders'
import Portal from 'components/Portal/Portal'
import { ConfirmDialog } from 'components/Modal/Modal'

const ManageRecordUserAccess = (props) => {
  const dispatch = useDispatch()
  const { sourceTypeId, sourceId, uccRecordId } = props
  const [showRevokedUsers, setShowRevokedUsers] = useState(false)
  const [showRejectedUsers, setShowRejectedUsers] = useState(false)
  const [loading, setLoading] = useState(false)
  const [rows, setRows] = useState([])
  const [roles, setRoles] = useState([])
  const [showConfirmationPopup, setShowConfirmationPopup] = useState(false)
  const [confirmationType, setConfirmationType] = useState(null)
  const [selectedRow, setSelectedRow] = useState(null)

  useEffect(() => {
    // Get rows
    getRows()
    // Get Record Access UserIndividualName Roles
    api.GET_RECORD_ACCESS_USER_ROLES_BY_SOURCE_TYPE(
      sourceTypeId,
      (res) => {
        setRoles(
          res
            .sort((a, b) => a.Name.localeCompare(b.Name))
            .map((role) => {
              return { ...role, label: role.Name, value: role.RecordAccessUserRoleId }
            })
        )
      },
      (err) => {
        console.error(err)
        dispatch(setErrorToast('Failed to retrieve Record Access UserIndividualName Roles'))
      }
    )
  }, [])

  useEffect(() => {
    if (rows.find((row) => !row.RecordAccessUserRoleId)) {
      let defaultRole = roles.find((role) => role.UseAsRequestAccessDefault)
      if (!defaultRole) {
        defaultRole = roles[0]
      }
      setRows((prevRows) => {
        return prevRows.map((row) => {
          if (!row.RecordAccessUserRoleId) {
            return { ...row, RecordAccessUserRoleId: defaultRole.RecordAccessUserRoleId }
          }
          return row
        })
      })
    }
  }, [roles, rows])

  const filteredRows = useMemo(() => {
    return rows
      .filter((r) => showRevokedUsers || !r.IsRevoked)
      .filter((r) => showRejectedUsers || r.Status !== 'Rejected')
  }, [rows, showRevokedUsers, showRejectedUsers])

  const columns = [
    {
      field: 'UserIndividualName',
      flex: 1,
      headerName: 'User',
      disableColumnMenu: true,
      renderCell: (params) => {
        return `${params.row.UserIndividualName} (${params.row.Username})`
      },
    },
    {
      field: 'Status',
      headerName: 'Status',
      disableColumnMenu: true,
    },
    {
      field: 'RecordAccessUserRoleId',
      flex: 1,
      headerName: 'Access Level',
      disableColumnMenu: true,
      sortable: false,
      renderCell: (params) => {
        const status = params.row.Status
        return (
          <div className="row-action-buttons">
            <SelectBox
              aria-label={`Access Level - ${params.row.Username}`}
              options={roles}
              noPlaceholder={roles.length > 0}
              onChange={(e) => {
                const updatedRow = {
                  ...params.row,
                  RecordAccessUserRoleId: Number(e),
                  IsDirty: true,
                }
                setRows((prevRows) => {
                  return prevRows.map((r) =>
                    r.RecordAccessId === updatedRow.RecordAccessId &&
                    r.RecordAccessRequestId === updatedRow.RecordAccessRequestId
                      ? updatedRow
                      : r
                  )
                })
              }}
              value={params.row.RecordAccessUserRoleId || ''}
              disabled={params.row.IsRevoked || status === 'Rejected'}
            />
            {params.row.IsDirty && params.row.Status !== 'Pending' && (
              <ContextButton onClick={() => clickUpdate(params.row)}>Apply</ContextButton>
            )}
          </div>
        )
      },
    },
    {
      field: 'ActionDate',
      flex: 0.5,
      headerName: 'Date',
      disableColumnMenu: true,
    },
    {
      field: 'Actions',
      flex: 1,
      headerName: 'Actions',
      disableColumnMenu: true,
      sortable: false,
      renderCell: (params) => {
        const status = params.row.Status
        return (
          <div className="row-action-buttons">
            {status === 'Pending' && (
              <ContextButton variant="success" onClick={() => clickAccept(params.row)}>
                Accept
              </ContextButton>
            )}
            {status === 'Pending' && (
              <ContextButton
                variant="danger"
                onClick={() => {
                  setSelectedRow(params.row)
                  setShowConfirmationPopup(true)
                  setConfirmationType('Reject')
                }}
              >
                Reject
              </ContextButton>
            )}
            {status === 'Approved' && (
              <ContextButton
                onClick={() => {
                  setSelectedRow(params.row)
                  setShowConfirmationPopup(true)
                  setConfirmationType('Revoke')
                }}
              >
                Revoke
              </ContextButton>
            )}
            {params.row.IsRevoked || status === 'Rejected' ? 'N/A' : null}
          </div>
        )
      },
    },
  ]

  const getRows = () => {
    setLoading(true)
    api.GET_RECORD_USER_ACCESS_ENTRIES(
      sourceTypeId,
      sourceTypeId === 1999 && uccRecordId ? uccRecordId : sourceId,
      (res) => {
        setRows(res)
        setLoading(false)
      },
      (err) => {
        console.error(err)
        dispatch(setErrorToast('Failed to retrieve users.'))
        setLoading(false)
      }
    )
  }

  const clickAccept = (row) => {
    // Implement accept logic here
    setLoading(true)
    api.ACCEPT_RECORD_ACCESS_REQUEST(
      row.RecordAccessRequestId,
      row.RecordAccessUserRoleId,
      (res) => {
        // Handle successful acceptance
        dispatch(setSuccessToast('Access request accepted successfully.'))
        getRows()
      },
      (err) => {
        // Handle error
        console.error(err)
        dispatch(setErrorToast('Failed to accept access request.'))
        setLoading(false)
      }
    )
  }

  const clickReject = (row) => {
    // Implement reject logic here
    setLoading(true)
    api.REJECT_RECORD_ACCESS_REQUEST(
      row.RecordAccessRequestId,
      (res) => {
        // Handle successful rejection
        getRows()
        dispatch(setSuccessToast('Access request rejected successfully.'))
      },
      (err) => {
        // Handle error
        dispatch(setErrorToast('Failed to reject access request.'))
        setLoading(false)
      }
    )
  }

  const clickRevoke = (row) => {
    // Implement revoke logic here
    setLoading(true)
    api.REVOKE_RECORD_ACCESS(
      row.RecordAccessId,
      (res) => {
        // Handle successful revocation
        dispatch(setSuccessToast('User access revoked successfully.'))
        getRows()
      },
      (err) => {
        // Handle error
        dispatch(setErrorToast(err.message || 'Failed to revoke user access.'))
        setLoading(false)
      }
    )
  }

  const clickUpdate = (row) => {
    // Implement update logic here
    setLoading(true)
    api.UPDATE_RECORD_USER_ACCESS(
      row.RecordAccessId,
      row.RecordAccessUserRoleId,
      (res) => {
        // Handle successful update
        dispatch(setSuccessToast('User access updated successfully.'))
        getRows()
      },
      (err) => {
        // Handle error
        dispatch(setErrorToast(err.message || 'Failed to update user access.'))
        setLoading(false)
      }
    )
  }

  return (
    <div className="modal-content">
      {loading && (
        <div
          style={{
            margin: '15px',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Spinner themeColor="dark-neutral" size="small" />
        </div>
      )}
      <div style={{ display: 'flex', gap: '10px' }}>
        <ButtBox
          label="Show Revoked Users"
          checked={showRevokedUsers}
          onChange={() => {
            setShowRevokedUsers(!showRevokedUsers)
          }}
        />
        <ButtBox
          label="Show Rejected Users"
          checked={showRejectedUsers}
          onChange={() => {
            setShowRejectedUsers(!showRejectedUsers)
          }}
        />
      </div>
      <GraniteDataGrid
        columns={columns}
        rows={filteredRows}
        getRowId={(r) =>
          r.RecordAccessRequestId ? `r-${r.RecordAccessRequestId}` : `a-${r.RecordAccessId}`
        }
        rowsPerPageOptions={[5]}
        pageSize={5}
      />
      <Portal isOpened={showConfirmationPopup}>
        <ConfirmDialog
          closeModal={() => setShowConfirmationPopup(false)}
          onModalClosed={() => {
            setSelectedRow(null)
            setConfirmationType(null)
          }}
          message={`Are you sure you want to ${confirmationType} this ${
            confirmationType === 'Reject' ? 'request' : 'access'
          } for ${
            selectedRow && selectedRow.UserIndividualName ? selectedRow.UserIndividualName : ''
          }?`}
          buttons={[
            {
              label: 'No',
              onClick: () => {
                setSelectedRow(null)
                setConfirmationType(null)
              },
              className: 'btn-default',
            },
            {
              label: 'Yes',
              onClick:
                confirmationType === 'Reject'
                  ? () => clickReject(selectedRow)
                  : () => clickRevoke(selectedRow),
              className: 'btn-danger',
            },
          ]}
          modalStyle={{ borderRadius: 3 }}
        />
      </Portal>
    </div>
  )
}

export default ManageRecordUserAccess

// const dummyRows = [
//   {
//     RecordAccessId: 16,
//     RecordAccessRequestId: null,
//     Username: 'stephen.blakeslee@tecuity.com',
//     UserIndividualName: 'Steve Blakeslee',
//     Status: 'Approved',
//     RecordAccessUserRoleId: 2,
//     ActionDate: '11/03/2025',
//     IsRevoked: false,
//   },
//   {
//     RecordAccessId: null,
//     RecordAccessRequestId: 23,
//     Username: 'justin.fairbourn@tecuity.com',
//     UserIndividualName: 'Justin Fairbourn',
//     Status: 'Pending',
//     RecordAccessUserRoleId: null,
//     ActionDate: '11/03/2025',
//     IsRevoked: false,
//   },
//   {
//     RecordAccessId: 40,
//     RecordAccessRequestId: null,
//     Username: 'justin.fairbourn@tecuity.com',
//     UserIndividualName: 'Justin Fairbourn',
//     Status: 'Revoked',
//     RecordAccessUserRoleId: null,
//     ActionDate: '11/03/2025',
//     IsRevoked: true,
//   },
//   {
//     RecordAccessId: null,
//     RecordAccessRequestId: 40,
//     Username: 'theo.myth@nowhere.not',
//     UserIndividualName: 'Theo Myth',
//     Status: 'Rejected',
//     RecordAccessUserRoleId: null,
//     ActionDate: '11/03/2025',
//     IsRevoked: false,
//   },
// ]

// const dummyRoles = [
//   {
//     RecordAccessUserRoleId: 3,
//     Name: 'Owner',
//     SourceTypeId: 0,
//     UseAsRequestAccessDefault: false,
//   },
//   {
//     RecordAccessUserRoleId: 4,
//     Name: 'User',
//     SourceTypeId: 0,
//     UseAsRequestAccessDefault: true,
//   },
//   {
//     RecordAccessUserRoleId: 5,
//     Name: 'Logged In Default',
//     SourceTypeId: 0,
//     UseAsRequestAccessDefault: false,
//   },
//   {
//     RecordAccessUserRoleId: 6,
//     Name: 'Not Logged In Default',
//     SourceTypeId: 0,
//     UseAsRequestAccessDefault: false,
//   },
//   {
//     RecordAccessUserRoleId: 2,
//     Name: 'Steve Test - Highest Role',
//     SourceTypeId: 54,
//     UseAsRequestAccessDefault: true,
//   },
// ]
